/**
 * Study Mode Pricing component - Pricing section for landing page
 */

import {Link} from 'react-router-dom';
import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {Card} from '../ui/Card';
import {Button} from '../ui/Button';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for trying out Study Mode',
    features: [
      '5 documents/month',
      'Basic AI analysis',
      'Flashcard generation',
      'Chat assistance',
      'Community support',
    ],
    cta: {label: 'Start Free', href: ROUTES.SIGN_IN},
  },
  {
    name: 'Premium',
    price: '$9.99',
    period: 'month',
    description: 'For serious students',
    features: [
      'Unlimited documents',
      'Advanced AI analysis',
      'Voice assistant mode',
      'Visual diagram generation',
      'Spaced repetition',
      'Priority support',
    ],
    cta: {label: 'Get Started', href: `${ROUTES.SIGN_IN}?plan=study-premium`},
    popular: true,
  },
  {
    name: 'Student',
    price: '$4.99',
    period: 'month',
    description: 'Special pricing for students',
    features: [
      'Unlimited documents',
      'All Premium features',
      'Student verification required',
      'Academic year pricing',
    ],
    cta: {label: 'Verify Student', href: `${ROUTES.SIGN_IN}?plan=study-student`},
    badge: 'Best Value',
  },
];

export function StudyPricing() {
  const {ref, isVisible} = useScrollAnimation();

  return (
    <Section id="pricing" padding="lg" background="white" ref={ref} className="bg-background dark:bg-[#1E1E1E]">
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-orange-600 dark:text-orange-500 mb-3 uppercase tracking-wide">
              Pricing
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Simple, Student-Friendly Pricing
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585] max-w-2xl mx-auto">
              Start free, upgrade when you're ready. All plans include our core features.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            {plans.map((plan, index) => (
              <div
                key={plan.name}
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{transitionDelay: `${index * 0.1}s`}}
              >
                <Card
                  hover={plan.popular}
                  className={`p-8 h-full flex flex-col relative transition-all duration-300 ${
                    plan.popular 
                      ? 'border-2 border-gray-900 dark:border-gray-700 shadow-xl dark:shadow-gray-900/50 scale-105' 
                      : 'border border-gray-200 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-gray-900/30'
                  }`}
                >
                  {(plan.popular || plan.badge) && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-md">
                        {plan.popular ? 'Popular' : plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col h-full">
                    {/* Plan Name */}
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
                        {plan.name}
                      </h3>
                      <p className="text-sm text-textSecondary dark:text-[#858585]">
                        {plan.description}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-textPrimary dark:text-[#CCCCCC]">
                          {plan.price}
                        </span>
                        {plan.period !== 'forever' && (
                          <span className="text-textSecondary dark:text-[#858585]">
                            /{plan.period}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="flex-1 space-y-3 mb-8">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm text-textSecondary dark:text-[#858585]">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Link to={plan.cta.href} className="mt-auto">
                      <Button
                        variant={plan.popular ? 'primary' : 'outline'}
                        size="md"
                        className="w-full"
                      >
                        {plan.cta.label}
                      </Button>
                    </Link>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          {/* Link to full pricing page */}
          <div className="text-center">
            <Link to={ROUTES.STUDY_MODE_PRICING} className="text-orange-600 dark:text-orange-500 hover:underline text-sm font-medium">
              View detailed pricing →
            </Link>
          </div>
        </div>
      </Container>
    </Section>
  );
}

